/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SearchIcon } from "../../../../../emotion";
import type { ThemeType } from "../../../../../global/emotion";
import { numericPixels, spacing, stroke } from "../../../../../global/emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../../_base/icons/Icon/GeneralIcons";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../_base/shadcn/command.emotion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../_base/shadcn/popover.emotion";
import { Loader } from "../../other/Loader/Loader.emotion";
import type { TextInputProps } from "../TextInput/TextInput.emotion";
import { TextInput } from "../TextInput/TextInput.emotion";
import type { ComboboxBaseProps } from "./types";

export type { ComboboxOption } from "./types";

const inputContainerStyle: Interpolation = {
  position: "relative",
  width: spacing.full,
};

const inputStyle: Interpolation<ThemeType> = (theme) => ({
  width: spacing.full,
  "&:focus": {
    borderColor: theme.semanticColors.neutralsLowest,
  },
});

const selectedValueContainerStyle: Interpolation<ThemeType> = (theme) => ({
  width: spacing.full,
  display: "flex",
  alignItems: "center",
  padding: `${spacing.ms} ${spacing.m}`,
  paddingLeft: `60px`,
  border: `${stroke.regular} solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: theme.radius.ms,
  backgroundColor: theme.semanticColors.background,
  cursor: "pointer",
  height: numericPixels[40],
  minHeight: numericPixels[64],
  color: theme.semanticColors.primary,
  outline: "none",
  transition: "border 200ms ease-in",
  boxSizing: "border-box",
  fontSize: "16px",
  lineHeight: "20px",
  ":hover:not(:disabled):not(:focus)": {
    borderColor: theme.semanticColors.neutralsVeryLow,
  },
  ":disabled": {
    color: theme.semanticColors.neutralsMedHigh,
    cursor: "not-allowed",
  },
  ":focus": {
    borderColor: theme.semanticColors.neutralsLowest,
    outline: "none",
  },
});

const selectedIconStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  left: spacing.m,
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none" as const,
});

const selectedLabelStyle: Interpolation = {
  display: "block",
  position: "relative",
  width: "100%",
  minHeight: "64px",
  opacity: 1,
  transition: "opacity 250ms",
};

const commandInputStyle: Interpolation<ThemeType> = () => ({
  display: "none", // hide the command's input, we use our own, but need to keep the component for cmdk to work properly
});

const loadingContainerStyle: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing.m,
});

export const popoverContentStyle = {
  padding: 0,
};

export const checkmarkIconStyle = (isSelected: boolean) => ({
  marginLeft: "auto",
  opacity: isSelected ? 1 : 0,
  transition: "opacity 150ms",
});

export interface ComboboxSearchConfig {
  /** Text shown in input when empty */
  placeholder?: string;
  /** Text shown when no results found */
  emptyText?: string;
  /** Text shown while loading */
  loadingText?: string;
  /** Width of the combobox */
  width?: string | number;
  /** Whether to filter options locally (set to false for async search) */
  filterLocally?: boolean;
  /** Whether to show the selected option's ReactNode label when selected */
  showSelectedLabel?: boolean;
  /** Disable the input */
  disabled?: boolean;
  /** Keep placeholder visible when focused */
  isPersistentLabel?: boolean;
}

export interface ComboboxSearchProps<T = string>
  extends ComboboxBaseProps<T>,
    Pick<TextInputProps, "startIcon" | "className"> {
  /** Controlled input value for external state management */
  inputValue?: string;
  /** Called when input value changes */
  onInputChange?: (newInputValue: string) => void;
  /** Called when an option is selected */
  onChange?: (value: T | undefined) => void;
  /** Shows loading spinner in dropdown */
  isLoading?: boolean;
  /** Configuration options */
  config?: ComboboxSearchConfig;
  /** Style overrides */
  customCss?: {
    commandItem?: Interpolation<ThemeType>;
  };
}

export function ComboboxSearch<T = string>({
  startIcon = <SearchIcon />,
  inputValue,
  onInputChange,
  value: controlledValue,
  onChange: onValueChange,
  options,
  isLoading = false,
  config = {},
  customCss = {},
  ...props
}: ComboboxSearchProps<T>) {
  const {
    placeholder,
    emptyText,
    loadingText,
    width = "100%",
    disabled = false,
    isPersistentLabel = false,
    filterLocally = true,
    showSelectedLabel = false,
  } = config;
  const [open, setOpen] = useState(false);
  const [internalInputValue, setInternalInputValue] = useState("");
  const [internalValue, setInternalValue] = useState<T | undefined>(undefined);
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);

  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const currentInputValue =
    inputValue !== undefined ? inputValue : internalInputValue;

  // Create a map from string keys to options for lookup
  const optionsByKey = useMemo(
    () => new Map(options.map((opt, idx) => [`option-${idx}`, opt])),
    [options],
  );

  const selectedOption = options.find(
    (option) => option.value.toString() === value?.toString(),
  );

  const filteredOptions = useMemo(() => {
    if (!filterLocally || !currentInputValue) return options;
    const searchLower = currentInputValue.toLowerCase();
    return options.filter((opt) => {
      const label =
        opt.text ??
        (typeof opt.label === "string" ? opt.label : String(opt.value));
      return (
        label.toLowerCase().includes(searchLower) ||
        String(opt.value).toLowerCase().includes(searchLower)
      );
    });
  }, [options, currentInputValue, filterLocally]);

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    if (onInputChange) {
      onInputChange(newValue);
    } else {
      setInternalInputValue(newValue);
    }
    if (!open) {
      setOpen(true);
    }
  };

  const handleSelect = (selectedKey: string) => {
    const selected = optionsByKey.get(selectedKey);
    if (selected) {
      if (controlledValue === undefined) {
        setInternalValue(selected.value);
      }
      onValueChange?.(selected.value);

      // Only clear internal input state (not controlled)
      // Parent can decide whether to clear controlled inputValue
      if (!onInputChange) {
        setInternalInputValue("");
      }
    }
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown" || e.key === "Enter") {
      if (!open) {
        setOpen(true);
      }
    }
  };

  const comboboxContentStyle = useMemo(
    () => ({
      ...popoverContentStyle,
      // Use Radix CSS variable to match trigger width
      width: "var(--radix-popover-trigger-width)",
      minWidth: typeof width === "number" ? `${width}px` : width,
    }),
    [width],
  );

  // Get display text for selected option
  const selectedDisplayText = selectedOption
    ? (selectedOption.text ??
      (typeof selectedOption.label === "string"
        ? selectedOption.label
        : String(selectedOption.value)))
    : "";

  // Display logic:
  // - When dropdown is open: show what user is typing
  // - When dropdown is closed with selection: show selected value
  // - When dropdown is closed without selection: show input (or empty for placeholder)
  const displayValue = open
    ? currentInputValue
    : selectedDisplayText || currentInputValue;

  const shouldShowRichLabel = showSelectedLabel && selectedOption && !open;

  // Focus input when dropdown opens
  useEffect(() => {
    if (open && !shouldShowRichLabel && inputRef.current) {
      // Small timeout to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [open, shouldShowRichLabel]);

  // Clear input and show search box when opening from selected state
  const handleOpenFromSelected = useCallback(() => {
    setOpen(true);
    if (onInputChange) {
      onInputChange("");
    } else {
      setInternalInputValue("");
    }
  }, [onInputChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild onClick={(e) => e.preventDefault()}>
        <div
          css={[
            inputContainerStyle,
            { width: typeof width === "number" ? `${width}px` : width },
          ]}
        >
          {shouldShowRichLabel ? (
            // Wrap in a label to match TextInput structure
            <label css={selectedLabelStyle}>
              <div css={selectedIconStyle}>{startIcon}</div>
              <div
                css={selectedValueContainerStyle}
                onClick={handleOpenFromSelected}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleOpenFromSelected();
                    e.preventDefault();
                  } else if (
                    e.key.length === 1 &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.altKey
                  ) {
                    // If user starts typing, open dropdown and set initial search
                    handleOpenFromSelected();
                    setTimeout(() => {
                      if (onInputChange) {
                        onInputChange(e.key);
                      } else {
                        setInternalInputValue(e.key);
                      }
                      inputRef.current?.focus();
                    }, 50);
                  }
                }}
              >
                {selectedOption.label}
              </div>
            </label>
          ) : (
            // Show the regular text input
            <TextInput
              ref={inputRef}
              placeholder={placeholder}
              value={displayValue}
              onInput={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setOpen(true)}
              startIcon={startIcon}
              isPersistentLabel={isPersistentLabel && !!placeholder}
              disabled={disabled}
              css={[inputStyle]}
              {...props}
            />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        css={[comboboxContentStyle]}
        onOpenAutoFocus={(e) => e.preventDefault()} // keep focus on input
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={currentInputValue}
            wrapperCss={commandInputStyle}
          />
          <CommandList>
            {isLoading ? (
              <div css={loadingContainerStyle}>
                <Loader size={20} />
                <span css={{ marginLeft: spacing.s }}>
                  {loadingText ?? t("loading_general_word")}
                </span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <CommandEmpty>{emptyText ?? t("search_no_results")}</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const key = `option-${options.indexOf(option)}`;
                  const isSelected = value === option.value;
                  return (
                    <CommandItem
                      key={key}
                      value={key}
                      onSelect={handleSelect}
                      data-selected={isSelected}
                      css={customCss?.commandItem}
                    >
                      {option.label ?? (
                        <Text as="span" type="body">
                          {option.text}
                        </Text>
                      )}
                      <CheckmarkIcon.SimpleOn
                        css={checkmarkIconStyle(isSelected)}
                        aria-hidden="true"
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
